/**
 * The locate resolver fed from live data, for the Overview (twin slice 1,
 * plan W4 step 10): what a link's focus is now, and where it lands.
 *
 * `resolveLocate` is pure (`locate-resolve.ts`); this hook hands it the
 * console's own lists and lookups -- the roster, W3's presence, the cars,
 * the door REGISTRY (the live door record has no level), the places, the
 * site's floors, the alert by id, the job from the live list (its place
 * from its phases, or from its dispatch request when they are empty), and
 * what the view on screen draws -- and re-asks whenever any of them moves.
 * It moves nothing: the Overview takes a floor from the answer once, at
 * the landing (`overview-landing.ts`).
 *
 * "Finding X…" lasts at most `FIND_TIMEOUT_MS` from the moment the focus
 * appeared; a boundary tick re-renders at that instant, so the line turns
 * into "Couldn't find X in the live data." with no new data.
 */
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type { LiftViewModel } from '../../api/adapters';
import { useAlert, useTasks } from '../../api/hooks';
import { usePlaces } from '../../api/hooks/usePlaces';
import { useTaskRequest } from '../../api/hooks/useTaskRequest';
import { doorsRegistryQueryOptions } from '../../api/query-options';
import type { RobotStateView } from '../../api/rmf-client';
import type { SiteId } from '../../api/v1';
import { monotonicNow, useBoundaryTick, useMinuteTick } from '../../state/clock';
import type { LocateFocus } from '../../state/locate';
import {
  resolveLocate,
  type LocateAlert,
  type LocateInventory,
  type LocateResolution,
  type LocateTask,
  type RecordLookup,
} from '../../state/locate-resolve';
import { useActiveSite } from '../../state/useActiveSite';
import type { RobotPresence } from '../Map/robot-presence';
import { resolveSiteTimeZone } from '../Tasks/site-timezone';
import { plannedPlaces } from '../Tasks/task-planned-places';

export interface OverviewLocateArgs {
  readonly siteId: SiteId;
  /** The link's focus; null for none (nothing is resolved). */
  readonly focus: LocateFocus | null;
  /** The link's `level`, as written. Only a hint. */
  readonly levelHint: string | null;
  /** Monotonic ms at which this focus appeared. */
  readonly startedAtMs: number;
  /** The site's floors, in building order. */
  readonly levels: readonly string[] | undefined;
  readonly robots: readonly RobotStateView[] | undefined;
  readonly lifts: readonly LiftViewModel[] | undefined;
  readonly presence: ReadonlyMap<string, RobotPresence>;
  /** What the view on screen draws; null before it has said. */
  readonly inventory: LocateInventory | null;
}

const LOADING = { state: 'loading' } as const;

export function useOverviewLocate(args: OverviewLocateArgs): LocateResolution | null {
  const { siteId, focus } = args;
  const { sites } = useActiveSite();
  const places = usePlaces();
  const tasks = useTasks();
  const doorRegistry = useQuery(doorsRegistryQueryOptions(siteId));
  const alert = useAlert(focus?.kind === 'alert' ? focus.alertId : null);
  // The job a link names, directly or through its alert, whose place may
  // only be on its dispatch request (a queued task publishes no phases).
  const alertTaskId =
    alert.state === 'found' && alert.alert.sourceKind === 'task' ? alert.alert.sourceId : null;
  const taskId = focus?.kind === 'task' ? focus.taskId : (alertTaskId ?? null);
  const request = useTaskRequest(taskId);
  // Names the day of a time that is not today; never subtracted.
  const wallNowMs = useMinuteTick();

  const doors = useMemo(
    () => doorRegistry.data?.map((d) => ({ name: d.id.door, level: d.level })),
    [doorRegistry.data],
  );
  const placeRows = useMemo(
    () => places.data?.map((p) => ({ name: p.name, level: p.level })),
    [places.data],
  );

  const alertLookup = useCallback(
    (id: string): RecordLookup<LocateAlert> => {
      if (focus?.kind !== 'alert' || id !== focus.alertId) return LOADING;
      switch (alert.state) {
        case 'found':
          return { state: 'found', record: alert.alert };
        case 'gone':
        case 'forbidden':
          return { state: alert.state };
        case 'error':
          return { state: 'error' };
        default:
          return LOADING;
      }
    },
    [alert, focus],
  );

  // A job is read from the live list only, so it is never `gone`: the
  // catalogue's "That job is no longer on record." (a purged job) has no
  // source here. No endpoint reads one task by id, and the dispatch
  // request's 404 is not one either: an RMF-originated job (a charge
  // retreat, an idle park) never had a request (`fetchTaskRequest`). A job
  // missing from the list reads "Couldn't find that job in the live data."
  // after the 5 s, which is true. A deviation, for the copy pass.
  const taskLookup = useCallback(
    (id: string): RecordLookup<LocateTask> => {
      const t = tasks.data?.find((row) => row.id === id);
      if (!t) return tasks.isError ? { state: 'error' } : LOADING;
      const fromPhases = t.phases?.map((p) => p.params?.place).find((z): z is string => Boolean(z));
      const req = id === taskId ? request.data?.request : undefined;
      const fromRequest = req
        ? (
            plannedPlaces(req.category ?? t.type, req).find((p) => p.role === 'zone') ??
            plannedPlaces(req.category ?? t.type, req)[0]
          )?.place
        : undefined;
      return {
        state: 'found',
        record: {
          id: t.id,
          status: t.status,
          assignedRobot: t.assignedRobot ?? null,
          zone: fromPhases ?? fromRequest ?? null,
          completedAtMs: t.completedAtMs ?? null,
        },
      };
    },
    [tasks.data, tasks.isError, taskId, request.data],
  );

  const zone = resolveSiteTimeZone(sites, siteId);
  const resolution = focus
    ? resolveLocate({
        focus,
        levelHint: args.levelHint,
        elapsedMs: monotonicNow() - args.startedAtMs,
        robots: args.robots,
        presence: args.presence,
        lifts: args.lifts,
        doors,
        places: placeRows,
        levels: args.levels,
        alert: alertLookup,
        task: taskLookup,
        inventory: args.inventory,
        clock: zone,
        wallNowMs,
      })
    : null;
  // "Finding" turns into "Couldn't find" at its boundary, with no new data.
  useBoundaryTick(resolution?.status === 'finding' ? resolution.msUntilChange : null);
  return resolution;
}
